import { i18n } from '@app/icare/i18n/i18n'
import type { AppRouterContext } from '@app/icare/router'
import NiceModal from '@ebay/nice-modal-react'
import { TanStackDevtools } from '@tanstack/react-devtools'
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools'
import { createRootRouteWithContext, HeadContent, Outlet, redirect, Scripts } from '@tanstack/react-router'
import { App as AntApp } from 'antd'
import 'dayjs/locale/de.js'
import { deDEIntl, ProConfigProvider } from '@ant-design/pro-components'
import appCss from '@app/icare/assets/app.css?url'
import { localQueryClient } from '@app/icare/client/localQueryClient'
import { Login } from '@app/icare/routes/_app/-login'
import AuthGate from '@app/icare/shared/auth/AuthGate'
import { antTheme } from '@app/icare/ui/theme'
import { appValueTypeMap } from '@app/icare/ui/valueTypes'
import { I18nProvider } from '@lingui/react'
import { renderEmpty } from '@pkg/ui/common'
import { DevtoolsLayoutOffset } from '@pkg/ui/devtools/DevtoolsLayoutOffset'
import { NotFoundErrorComponent } from '@pkg/ui/error'
import { DefaultErrorComponent } from '@pkg/ui/error/DefaultErrorComponent'
import { ResponsiveConfigProvider } from '@pkg/ui/theme'
import { ToastBridge } from '@pkg/ui/toast'
import { formDevtoolsPlugin } from '@tanstack/react-form-devtools'
import { QueryClientProvider } from '@tanstack/react-query'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import antdCss from 'antd/dist/antd.css?url'
import antdResetCss from 'antd/dist/reset.css?url'
import deDE from 'antd/locale/de_DE.js'
import type { ReactNode } from 'react'

export const Route = createRootRouteWithContext<AppRouterContext>()({
  ssr: false,
  beforeLoad: ({ location }) => {
    // /login no longer exists — auth UI is rendered out-of-router via AuthProvider.
    // Catch stale browser caches/bookmarks and send them home silently.
    if (location.pathname === '/login') {
      throw redirect({ to: '/', replace: true })
    }
  },
  errorComponent: DefaultErrorComponent,
  notFoundComponent: NotFoundErrorComponent,
  component: RootComponent,
  shellComponent: RootShell,
  head: () => ({
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        title: 'iCare',
      },
      {
        name: 'viewport',
        content: 'width=device-width, initial-scale=1',
      },
    ],
    links: [
      { rel: 'stylesheet', href: antdResetCss },
      { rel: 'stylesheet', href: antdCss },
      { rel: 'stylesheet', href: appCss },
      {
        rel: 'apple-touch-icon',
        sizes: '180x180',
        href: '/apple-touch-icon.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '32x32',
        href: '/favicon-32x32.png',
      },
      {
        rel: 'icon',
        type: 'image/png',
        sizes: '16x16',
        href: '/favicon-16x16.png',
      },
      { rel: 'manifest', href: '/manifest.webmanifest', color: '#fffff' },
      { rel: 'icon', href: '/favicon.ico' },
    ],
  }),
})

function RootShell(props: { children: ReactNode }) {
  const { children } = props
  return (
    <html lang="de">
      <head>
        <HeadContent />
      </head>
      <body>
        <I18nProvider i18n={i18n}>
          <QueryClientProvider client={localQueryClient}>
            <ResponsiveConfigProvider theme={antTheme} renderEmpty={renderEmpty} locale={deDE}>
              <ProConfigProvider intl={deDEIntl} token={antTheme.token} valueTypeMap={appValueTypeMap as never}>
                {children}
                <Scripts />
              </ProConfigProvider>
            </ResponsiveConfigProvider>
          </QueryClientProvider>
        </I18nProvider>
      </body>
    </html>
  )
}

function RootComponent() {
  return (
    <AntApp notification={{ placement: 'bottomRight', duration: 3, stack: { threshold: 3 } }}>
      <ToastBridge />
      <NiceModal.Provider />

      <TanStackDevtools
        plugins={[
          {
            name: 'TanStack Query',
            render: <ReactQueryDevtoolsPanel />,
            defaultOpen: true,
          },
          {
            name: 'TanStack Router',
            render: <TanStackRouterDevtoolsPanel />,
            defaultOpen: false,
          },
          formDevtoolsPlugin(),
        ]}
      />
      <DevtoolsLayoutOffset />
      <AuthGate authPage={<Login />}>
        <Outlet />
      </AuthGate>
    </AntApp>
  )
}
